import type { Vec3 } from "./types";
import { wrapAngle } from "./math";
import { yawFromAlpha } from "./camera_logic";
import type { InputState } from "./input";

export type LocoState = "idle" | "walk" | "run" | "jump" | "fall";

export interface PlayerConfig {
  walkSpeed: number;
  runSpeed: number;
  jumpVel: number;
  turnSpeed: number;
  height: number;
  radius: number;
  mass: number;
}

export const DEFAULT_PLAYER_CONFIG: PlayerConfig = {
  walkSpeed: 3.0,
  runSpeed: 6.0,
  jumpVel: 6.0,
  turnSpeed: 2.8,
  height: 1.7,
  radius: 0.35,
  mass: 70,
};

export const LOCOMOTION_CLIPS: Record<LocoState, string> = {
  idle: "Idle",
  walk: "Walking",
  run: "Goofy Running",
  jump: "Joyful Jump (1)",
  fall: "Falling",
};

export const EMOTE_BINDINGS: Record<string, string> = {
  Digit1: "Hip Hop Dancing",
  Digit2: "Rumba Dancing",
  Digit3: "Silly Dancing",
  Digit4: "Twist Dance",
  Digit5: "Dancing Twerk",
  Digit6: "Cow Milking",
  Digit7: "Pick Fruit",
  Digit8: "Throw Object",
  Digit9: "Standing Arguing",
  Digit0: "Catwalk Walk",
};

const BLEND_EPSILON = 0.02;
const BLEND_RATE_IN = 8;
const BLEND_RATE_OUT = 14;

export interface PlayerPhysicsState {
  position: Vec3;
  velocity: Vec3;
  grounded: boolean;
}

export interface PlayerState {
  yaw: number;
  hadForwardBackIntent: boolean;
  currentLoco: LocoState;
  activeEmote: string | null;
  emoteEndsAt: number;
  previousDigitKeys: Set<string>;
  animWeights: Map<string, number>;
}

export interface PlayerCommand {
  velocity: Vec3;
  rigYaw: number;
  jump: boolean;
  respawn: boolean;
  animResets: string[];
  pendingEmote: string | null;
}

export function createPlayerState(yaw = 0, animNames: string[] = []): PlayerState {
  const animWeights = new Map<string, number>();
  for (const name of animNames) animWeights.set(name, 0);
  return {
    yaw,
    hadForwardBackIntent: false,
    currentLoco: "idle",
    activeEmote: null,
    emoteEndsAt: 0,
    previousDigitKeys: new Set(),
    animWeights,
  };
}

export function updatePlayerLogic(
  dt: number,
  input: InputState,
  physics: PlayerPhysicsState,
  state: PlayerState,
  cameraAlpha: number | null,
  config: PlayerConfig,
): PlayerCommand {
  const animResets: string[] = [];
  let pendingEmote: string | null = null;

  const turnLeft = input.has("KeyA") || input.has("ArrowLeft");
  const turnRight = input.has("KeyD") || input.has("ArrowRight");

  let mz = 0;
  let mx = 0;
  if (input.has("KeyW") || input.has("ArrowUp")) mz += 1;
  if (input.has("KeyS") || input.has("ArrowDown")) mz -= 1;
  if (input.has("KeyE")) mx += 1;
  if (input.has("KeyQ")) mx -= 1;

  const hasForwardBackIntent = mz !== 0;

  if (turnLeft || turnRight || (hasForwardBackIntent && !state.hadForwardBackIntent)) {
    if (cameraAlpha !== null && Number.isFinite(cameraAlpha)) {
      state.yaw = wrapAngle(yawFromAlpha(cameraAlpha));
    }
  }
  state.hadForwardBackIntent = hasForwardBackIntent;

  if (turnLeft) state.yaw -= config.turnSpeed * dt;
  if (turnRight) state.yaw += config.turnSpeed * dt;

  const sinY = Math.sin(state.yaw);
  const cosY = Math.cos(state.yaw);
  const wishX = sinY * mz + cosY * mx;
  const wishZ = cosY * mz - sinY * mx;
  const wishLen = Math.sqrt(wishX * wishX + wishZ * wishZ);
  const hasMovementIntent = wishLen > 0;
  const normX = hasMovementIntent ? wishX / wishLen : 0;
  const normZ = hasMovementIntent ? wishZ / wishLen : 0;

  const running = input.has("ShiftLeft") || input.has("ShiftRight");
  const speed = running ? config.runSpeed : config.walkSpeed;

  const velocity: Vec3 = {
    x: normX * speed,
    y: physics.velocity.y,
    z: normZ * speed,
  };

  for (const [code, emoteName] of Object.entries(EMOTE_BINDINGS)) {
    const down = input.has(code);
    const wasDown = state.previousDigitKeys.has(code);
    if (down && !wasDown) {
      state.activeEmote = emoteName;
      state.emoteEndsAt = Number.MAX_SAFE_INTEGER;
      state.animWeights.set(emoteName, 1);
      pendingEmote = emoteName;
    }
    if (down) state.previousDigitKeys.add(code);
    else state.previousDigitKeys.delete(code);
  }

  const movementInterruptedEmote = hasMovementIntent && state.activeEmote !== null;
  if (movementInterruptedEmote) {
    state.activeEmote = null;
    state.emoteEndsAt = 0;
    pendingEmote = null;
  }

  const horizSpeed = Math.sqrt(physics.velocity.x ** 2 + physics.velocity.z ** 2);
  let loco: LocoState;
  if (!physics.grounded) {
    loco = physics.velocity.y > 0.5 ? "jump" : "fall";
  } else if (horizSpeed > 5) {
    loco = "run";
  } else if (horizSpeed > 0.3) {
    loco = "walk";
  } else {
    loco = "idle";
  }
  state.currentLoco = loco;

  let activeName: string;
  if (state.activeEmote && performance.now() < state.emoteEndsAt) {
    activeName = state.activeEmote;
  } else {
    state.activeEmote = null;
    activeName = LOCOMOTION_CLIPS[state.currentLoco];
  }

  if (movementInterruptedEmote) {
    for (const [name] of state.animWeights) {
      const isTarget = name === activeName;
      state.animWeights.set(name, isTarget ? 1 : 0);
      if (!isTarget) animResets.push(name);
    }
  } else {
    const inK = Math.min(1, dt * BLEND_RATE_IN);
    const outK = Math.min(1, dt * BLEND_RATE_OUT);
    for (const [name] of state.animWeights) {
      const isTarget = name === activeName;
      const k = isTarget ? inK : outK;
      const desired = isTarget ? 1 : 0;
      const current = state.animWeights.get(name) ?? 0;
      const newWeight = current + (desired - current) * k;
      state.animWeights.set(name, newWeight);
      if (!isTarget && newWeight < BLEND_EPSILON) {
        animResets.push(name);
      }
    }
  }

  const jump = input.has("Space") && physics.grounded;
  if (jump) input.delete("Space");

  const respawn = physics.position.y < -20;

  return { velocity, rigYaw: state.yaw, jump, respawn, animResets, pendingEmote };
}
